import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import config from 'capacitor.config';
import { flavourPageTitleKeys } from 'src/app/flavour-routes';

/** Maps route URL segments to i18n title keys (WCAG 2.4.2). */
const ROUTE_TITLE_KEYS: Array<{ match: string; key: string }> = [
  { match: '/v2/account/login', key: 'shared-page.title.login' },
  ...flavourPageTitleKeys,
  { match: '/v2/account/reset-password', key: 'shared-page.title.reset-password' },
  { match: '/v2/registration/profile-detail', key: 'shared-page.title.profile-detail' },
  { match: '/v2/registration/confirm-otp', key: 'shared-page.title.confirm-otp' },
  { match: '/v2/registration/consent', key: 'shared-page.title.consent' },
  { match: '/v2/registration/create-password', key: 'shared-page.title.create-password' },
  { match: '/v2/welcome/intro-page', key: 'shared-page.title.intro' },
  { match: '/v2/health-check/scan-intro', key: 'shared-page.title.scan-intro' },
  { match: '/v2/help-center', key: 'shared-page.title.help-center' },
  { match: '/home/healthScore', key: 'shared-page.title.health-score' },
  { match: '/home/dashboard', key: 'shared-page.title.dashboard' },
  { match: '/home/myProfile', key: 'shared-page.title.my-profile' },
  { match: '/home/notification', key: 'shared-page.title.notifications' },
  { match: '/home/wearables', key: 'shared-page.title.wearables' },
  { match: '/home/mediaChatbot', key: 'shared-page.title.media' },
  { match: '/home/media', key: 'shared-page.title.media' },
  { match: '/home/contact-us', key: 'shared-page.title.contact-us' },
  { match: '/home/change-password', key: 'shared-page.title.change-password' },
  { match: '/home/change-email', key: 'shared-page.title.change-email' },
  { match: '/home/verify-email', key: 'shared-page.title.verify-email' },
  { match: '/viva-lite/faq', key: 'shared-page.title.faq' }];

const DEFAULT_TITLE_KEY = 'shared-page.title.app';

@Injectable({ providedIn: 'root' })
export class PageTitleService {
  private started = false;

  constructor(
    private readonly title: Title,
    private readonly router: Router,
    private readonly translate: TranslateService
  ) {}

  /** Call once from AppComponent after translate is ready. */
  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.updateFromRoute());
    this.updateFromRoute();
  }

  updateFromRoute(): void {
    const url = this.router.url.split('?')[0];
    const entry = ROUTE_TITLE_KEYS.find((r) => url.includes(r.match));
    // Capacitor appName is flavour-specific (file-replaced at build); falls back to i18n.
    const appName =
      (typeof config.appName === 'string' && config.appName.trim()) ||
      this.translate.instant(DEFAULT_TITLE_KEY);

    if (!entry) {
      this.title.setTitle(appName);
      return;
    }

    const pageTitle = this.translate.instant(entry.key);
    this.title.setTitle(pageTitle === entry.key ? appName : `${pageTitle} | ${appName}`);
  }
}
